import { FC, useMemo } from 'react'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  ButtonIcon,
  CheckBox,
  CustomSelect,
  ErrorLabel,
  IOption,
  Input,
} from '../../../../../../../components'
import { IconPaths, IconSize } from '../../../../../../../../_metronic/helpers'
import {
  CondicaoCatalogo,
  VariavelCatalogo,
  operadoresPara,
} from '../../../../core/condicao/catalogo'
import {
  Fonte,
  Operador,
  TipoValor,
  ValorCondicao,
  tipoValorDoCampo,
} from '../../../../core/condicao/tipos'
import { ErroPosicionado } from '../../../../core/condicao/caminho'
import {
  CampoReferenciavel,
  LinhaEmEdicao,
  OpcaoReferencia,
  formatoDoOperador,
} from './tiposEditor'

type Props = {
  linha: LinhaEmEdicao
  /** Número da linha mostrado nos rótulos acessíveis (base 1). */
  numero: number
  campos: CampoReferenciavel[]
  catalogo: CondicaoCatalogo
  erros: ErroPosicionado[]
  disabled?: boolean
  onChange: (linha: LinhaEmEdicao) => void
  onRemover: () => void
}

type Referencia = {
  tipo: TipoValor | null
  multiplo: boolean
  /** Domínio fechado (opções do campo ou valores da variável); vazio = domínio livre. */
  dominio: OpcaoReferencia[]
}

const referenciaVazia: Referencia = { tipo: null, multiplo: false, dominio: [] }

const referenciaDe = (
  linha: LinhaEmEdicao,
  campos: CampoReferenciavel[],
  variaveis: VariavelCatalogo[]
): Referencia => {
  if (!linha.ref) return referenciaVazia
  if (linha.fonte === 'campo') {
    const campo = campos.find((c) => c.codigo === linha.ref)
    if (!campo) return referenciaVazia
    return {
      tipo: tipoValorDoCampo(campo.tipo),
      multiplo: campo.multiplo,
      dominio: campo.tipo === 'OPCAO' ? campo.opcoes : [],
    }
  }
  const variavel = variaveis.find((v) => v.codigo === linha.ref)
  if (!variavel) return referenciaVazia
  return {
    tipo: variavel.tipo,
    multiplo: false,
    dominio:
      variavel?.valoresPermitidos?.map((v) => ({ codigo: v.valor, descricao: v.rotulo })) ?? [],
  }
}

const comoLista = (valor: ValorCondicao | undefined): string[] =>
  Array.isArray(valor) ? valor.map((item) => String(item)) : []

const textoDaLista = (valor: ValorCondicao | undefined): string => comoLista(valor).join(', ')

export const CondicaoLinha: FC<Props> = ({
  linha,
  numero,
  campos,
  catalogo,
  erros,
  disabled,
  onChange,
  onRemover,
}) => {
  const { t } = useT()

  const referencia = useMemo(
    () => referenciaDe(linha, campos, catalogo.variaveis),
    [linha, campos, catalogo.variaveis]
  )

  const operadores = useMemo(
    () => (referencia.tipo ? operadoresPara(catalogo, referencia.tipo, referencia.multiplo) : []),
    [catalogo, referencia]
  )

  const formato = formatoDoOperador(linha.op)
  const idBase = `condicao-linha-${numero}`

  const opcoesFonte: IOption<Fonte>[] = [
    { value: 'campo', label: t('formularios.condicao.fonte.campo') },
    { value: 'variavel', label: t('formularios.condicao.fonte.variavel') },
  ]

  const opcoesRef = useMemo<IOption<string>[]>(
    () =>
      linha.fonte === 'campo'
        ? campos.map((campo) => ({
            value: campo.codigo,
            label: campo.codigo
              ? campo.rotulo
              : `${campo.rotulo} — ${t('formularios.condicao.ref.semCodigo')}`,
            isDisabled: !campo.codigo,
          }))
        : catalogo.variaveis.map((variavel) => ({
            value: variavel.codigo,
            label: variavel.rotulo,
          })),
    [linha.fonte, campos, catalogo.variaveis, t]
  )

  const opcoesOperador = useMemo<IOption<Operador>[]>(
    () => operadores.map((operador) => ({ value: operador.json, label: operador.simbolo })),
    [operadores]
  )

  const opcoesDominio = useMemo<IOption<string>[]>(
    () => referencia.dominio.map((opcao) => ({ value: opcao.codigo, label: opcao.descricao })),
    [referencia.dominio]
  )

  const trocarFonte = (fonte: Fonte) => onChange({ fonte, ref: '', op: '=' })

  const trocarRef = (ref: string) => {
    const proxima = referenciaDe({ ...linha, ref }, campos, catalogo.variaveis)
    const aceitos = proxima.tipo
      ? operadoresPara(catalogo, proxima.tipo, proxima.multiplo).map((o) => o.json)
      : []
    const op = aceitos.includes(linha.op) ? linha.op : aceitos[0] ?? '='
    const nova: LinhaEmEdicao = { fonte: linha.fonte, ref, op }
    return onChange(nova)
  }

  const trocarOperador = (op: Operador) => {
    const nova: LinhaEmEdicao = { fonte: linha.fonte, ref: linha.ref, op }
    if (formatoDoOperador(op) !== 'nenhum' && formatoDoOperador(op) === formato) {
      nova.valor = linha.valor
    }
    onChange(nova)
  }

  const trocarValor = (valor: ValorCondicao | undefined) => onChange({ ...linha, valor })

  const mensagens = erros.map((erro) => erro.mensagem).filter((mensagem) => !!mensagem)

  const renderValorEscalar = () => {
    if (referencia.dominio.length > 0) {
      return (
        <CustomSelect
          inputId={`${idBase}-valor`}
          ariaLabel={t('formularios.condicao.linha.valor', { numero })}
          placeholder={t('formularios.condicao.ref.placeholder')}
          isDisabled={disabled}
          options={opcoesDominio}
          value={opcoesDominio.find((opcao) => opcao.value === linha.valor)}
          onChange={(opcao: IOption<string> | null) => trocarValor(opcao?.value)}
        />
      )
    }
    if (referencia.tipo === 'NUMERO') {
      return (
        <Input
          id={`${idBase}-valor`}
          type='number'
          step='any'
          aria-label={t('formularios.condicao.linha.valor', { numero })}
          placeholder='Digite'
          disabled={disabled}
          value={typeof linha.valor === 'number' ? String(linha.valor) : ''}
          onChange={(e) => trocarValor(e.target.value === '' ? undefined : Number(e.target.value))}
        />
      )
    }
    return (
      <Input
        id={`${idBase}-valor`}
        aria-label={t('formularios.condicao.linha.valor', { numero })}
        placeholder='Digite'
        disabled={disabled}
        value={typeof linha.valor === 'string' ? linha.valor : ''}
        onChange={(e) => trocarValor(e.target.value === '' ? undefined : e.target.value)}
      />
    )
  }

  const renderValorIntervalo = () => {
    const intervalo = Array.isArray(linha.valor) ? (linha.valor as Array<number | string>) : []
    const atualizar = (indice: 0 | 1, bruto: string) => {
      const atual: Array<number | undefined> = [
        typeof intervalo[0] === 'number' ? (intervalo[0] as number) : undefined,
        typeof intervalo[1] === 'number' ? (intervalo[1] as number) : undefined,
      ]
      atual[indice] = bruto === '' ? undefined : Number(bruto)
      trocarValor(
        atual[0] === undefined && atual[1] === undefined
          ? undefined
          : ([atual[0] ?? 0, atual[1] ?? 0] as number[])
      )
    }
    return (
      <div className='d-flex gap-2'>
        <Input
          type='number'
          step='any'
          aria-label={t('formularios.condicao.linha.valor.inicio', { numero })}
          placeholder='Digite'
          disabled={disabled}
          value={typeof intervalo[0] === 'number' ? String(intervalo[0]) : ''}
          onChange={(e) => atualizar(0, e.target.value)}
        />
        <Input
          type='number'
          step='any'
          aria-label={t('formularios.condicao.linha.valor.fim', { numero })}
          placeholder='Digite'
          disabled={disabled}
          value={typeof intervalo[1] === 'number' ? String(intervalo[1]) : ''}
          onChange={(e) => atualizar(1, e.target.value)}
        />
      </div>
    )
  }

  const renderValorLista = () => {
    if (referencia.dominio.length > 0) {
      const escolhidos = comoLista(linha.valor)
      return (
        <fieldset className='border-0 p-0 m-0'>
          <legend className='fs-8 text-muted mb-1'>
            {t('formularios.condicao.linha.valor', { numero })}
          </legend>
          <div className='d-flex flex-wrap gap-3'>
            {referencia.dominio.map((opcao) => {
              const marcado = escolhidos.includes(opcao.codigo)
              return (
                <CheckBox
                  key={opcao.codigo}
                  id={`${idBase}-valor-${opcao.codigo}`}
                  label={opcao.descricao}
                  disabled={disabled}
                  checked={marcado}
                  onChange={() => {
                    const proximos = marcado
                      ? escolhidos.filter((item) => item !== opcao.codigo)
                      : [...escolhidos, opcao.codigo]
                    trocarValor(proximos.length === 0 ? undefined : proximos)
                  }}
                />
              )
            })}
          </div>
        </fieldset>
      )
    }
    return (
      <Input
        id={`${idBase}-valor`}
        aria-label={t('formularios.condicao.linha.valor', { numero })}
        placeholder={t('formularios.condicao.valor.em.ajuda')}
        disabled={disabled}
        value={textoDaLista(linha.valor)}
        onChange={(e) => {
          const itens = e.target.value
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item !== '')
          if (itens.length === 0) return trocarValor(undefined)
          return trocarValor(
            referencia.tipo === 'NUMERO'
              ? (itens.map((item) => Number(item.replace(',', '.'))) as number[])
              : itens
          )
        }}
      />
    )
  }

  return (
    <div className='condicao-linha' data-testid={`condicao-linha-${numero}`}>
      <div className='condicao-linha__cabecalho'>
        <span className='fs-7 fw-bolder'>{t('formularios.condicao.linha.titulo', { numero })}</span>
        <ButtonIcon
          className='p-0 m-0 ms-auto'
          icon={IconPaths.Trash}
          iconSize={IconSize.Small}
          disabled={disabled}
          ariaLabel={t('formularios.condicao.linha.remover', { numero })}
          onClick={onRemover}
        />
      </div>
      <div className='condicao-linha__campos'>
        <CustomSelect
          inputId={`${idBase}-fonte`}
          ariaLabel={t('formularios.condicao.linha.fonte', { numero })}
          isDisabled={disabled}
          isClearable={false}
          options={opcoesFonte}
          value={opcoesFonte.find((opcao) => opcao.value === linha.fonte)}
          onChange={(opcao: IOption<Fonte> | null) => opcao && trocarFonte(opcao.value)}
        />

        <CustomSelect
          inputId={`${idBase}-ref`}
          ariaLabel={t('formularios.condicao.linha.ref', { numero })}
          placeholder={t('formularios.condicao.ref.placeholder')}
          isDisabled={disabled}
          isClearable={false}
          options={opcoesRef}
          value={linha.ref ? opcoesRef.find((opcao) => opcao.value === linha.ref) : undefined}
          onChange={(opcao: IOption<string> | null) => opcao && trocarRef(opcao.value)}
        />

        <CustomSelect
          inputId={`${idBase}-op`}
          ariaLabel={t('formularios.condicao.linha.op', { numero })}
          placeholder=''
          isDisabled={disabled || operadores.length === 0}
          isClearable={false}
          options={opcoesOperador}
          value={opcoesOperador.find((opcao) => opcao.value === linha.op)}
          onChange={(opcao: IOption<Operador> | null) => opcao && trocarOperador(opcao.value)}
        />

        <div className='condicao-linha__valor'>
          {formato === 'escalar' && referencia.tipo && renderValorEscalar()}
          {formato === 'intervalo' && renderValorIntervalo()}
          {formato === 'lista' && renderValorLista()}
        </div>
      </div>
      {mensagens.map((mensagem) => (
        <ErrorLabel key={mensagem}>{mensagem}</ErrorLabel>
      ))}
    </div>
  )
}
